// src/components/production/DesviosDashSubTab.js
import React, { useState, useEffect, useMemo } from "react";
import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, ComposedChart, Legend 
} from "recharts";
import { 
  AlertTriangle, Calendar, Filter, Activity, Clock, ShieldAlert, 
  Layers, RefreshCw, BarChart2, TrendingUp, AlertCircle, ArrowLeft,
  Settings, Trash2, Edit, Check, X, Plus, Package, Search
} from "lucide-react";
import { pb } from "../../services/pocketbase";
import { MONITORED_STATUSES } from "../../config/monitoredStatuses";
import { PRODUCTION_STATUS_CONFIG, KANBAN_ORDER } from "../../config/productionStatuses";
import { desvioEventosService } from "../../services/desvioEventosService";

export default function DesviosDashSubTab({ user, findCatalogItem }) {
  const [eventos, setEventos] = useState([]);
  const [fluxoResumos, setFluxoResumos] = useState([]);
  const [loading, setLoading] = useState(false);

  // Subaba interna: "dash" ou "modelos" ou "config"
  const [qcSubTab, setQcSubTab] = useState("dash");

  // Estados Análise por Modelo
  const [selectedModelReport, setSelectedModelReport] = useState(null);
  const [searchModelName, setSearchModelName] = useState("");

  // Auxiliar para obter o nome do modelo/pai a partir do SKU (ignora medidas/tamanhos)
  const getModelFromSku = (sku) => {
    if (!sku) return "N/I";
    const catalogData = findCatalogItem ? findCatalogItem(sku) : null;
    if (catalogData && catalogData.model) return catalogData.model;
    if (catalogData && catalogData.baseSku) return catalogData.baseSku;

    // Fallback: divide por "-" e remove o último segmento se for numérico ou tamanho (comprimento até 2 letras)
    const parts = sku.split("-");
    if (parts.length > 1) {
      const lastPart = parts[parts.length - 1];
      if (!isNaN(lastPart) || lastPart.length <= 2) {
        return parts.slice(0, -1).join("-");
      }
    }
    return sku;
  };

  // Filtros Dashboard
  const [statusFiltro, setStatusFiltro] = useState("all");
  const [presetPeriodo, setPresetPeriodo] = useState("30"); // "7", "30", "90", "custom"
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [origemFiltro, setOrigemFiltro] = useState("all");
  const [categoriaFiltro, setCategoriaFiltro] = useState("all");

  // Pareto Drill-down state
  const [drilldownCategoria, setDrilldownCategoria] = useState(null);

  // --- ESTADOS DO GERENCIADOR DE CONFIGURAÇÃO (TAB CONFIG) ---
  const [categorias, setCategorias] = useState([]);
  const [problemasConfig, setProblemasConfig] = useState([]);
  
  // Categorias form states
  const [novaCatNome, setNovaCatNome] = useState("");
  const [editingCatId, setEditingCatId] = useState(null);
  const [editingCatNome, setEditingCatNome] = useState("");

  // Motivos form states
  const [novoProbNome, setNovoProbNome] = useState("");
  const [novoProbCatId, setNovoProbCatId] = useState("");
  const [novoProbVerificar, setNovoProbVerificar] = useState(false);
  const [novoProbFalhaBanca, setNovoProbFalhaBanca] = useState(false);
  const [novoProbObs, setNovoProbObs] = useState(false);
  
  // Edit motive modal state
  const [editingProblem, setEditingProblem] = useState(null); // Contém { id, nome, categoria, verificar, falhaBanca, obs_obrigatoria, ativo }

  // Filtros de busca no gerenciador
  const [searchMotive, setSearchMotive] = useState("");
  const [filterMotiveCat, setFilterMotiveCat] = useState("all");

  // Carrega os dados do Dashboard
  const loadData = async () => {
    setLoading(true);
    try {
      const records = await pb.collection("producao_desvio_eventos").getFullList({
        expand: "problema,problema.categoria,registrado_por",
        sort: "-created",
      });
      setEventos(records);

      const flowSummaries = await pb.collection("producao_fluxo_resumo").getFullList({
        sort: "-ano,-mes",
      });
      setFluxoResumos(flowSummaries);
    } catch (e) {
      console.error("Erro ao carregar dados do dashboard de desvios:", e);
    } finally {
      setLoading(false);
    }
  };

  // Carrega os dados do Gerenciador de Motivos
  const loadConfigData = async () => {
    setLoading(true);
    desvioEventosService.clearCache();
    try {
      const cats = await pb.collection("producao_problema_categorias").getFullList({
        sort: "nome",
      });
      const probs = await pb.collection("producao_problemas").getFullList({
        expand: "categoria",
        sort: "categoria.nome,nome",
      });
      setCategorias(cats);
      setProblemasConfig(probs);
    } catch (e) {
      console.error("Erro ao carregar configurações de motivos:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (qcSubTab === "dash") {
      loadData();
    } else {
      loadConfigData();
    }
  }, [qcSubTab]);

  // Define limites de datas baseados no preset
  useEffect(() => {
    if (presetPeriodo !== "custom") {
      const fim = new Date();
      const inicio = new Date();
      inicio.setDate(fim.getDate() - parseInt(presetPeriodo));
      
      setDataInicio(inicio.toISOString().split("T")[0]);
      setDataFim(fim.toISOString().split("T")[0]);
    }
  }, [presetPeriodo]);

  // --- CRUD CATEGORIAS ---
  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!novaCatNome.trim()) return;

    try {
      await pb.collection("producao_problema_categorias").create({
        nome: novaCatNome.toUpperCase().trim(),
        ativo: true,
      });
      setNovaCatNome("");
      await loadConfigData();
    } catch (err) {
      alert("Erro ao criar categoria: " + err.message);
    }
  };

  const handleUpdateCategory = async (id) => {
    if (!editingCatNome.trim()) return;
    try {
      await pb.collection("producao_problema_categorias").update(id, {
        nome: editingCatNome.toUpperCase().trim(),
      });
      setEditingCatId(null);
      setEditingCatNome("");
      await loadConfigData();
    } catch (err) {
      alert("Erro ao editar categoria: " + err.message);
    }
  };

  const handleToggleCategoryActive = async (cat) => {
    try {
      await pb.collection("producao_problema_categorias").update(cat.id, {
        ativo: !cat.ativo,
      });
      await loadConfigData();
    } catch (err) {
      alert("Erro ao mudar status da categoria: " + err.message);
    }
  };

  const handleDeleteCategory = async (id) => {
    // Busca se existem motivos vinculados a esta categoria
    const linkedProbs = problemasConfig.filter((p) => p.categoria === id);

    if (linkedProbs.length > 0) {
      const confirmCascade = window.confirm(
        `Esta categoria possui ${linkedProbs.length} motivo(s) associado(s) (ex: ${linkedProbs.slice(0, 3).map(p => p.nome).join(", ")}).\n\nDeseja excluir a categoria E todos os motivos associados de uma só vez (exclusão em cascata)?`
      );
      if (!confirmCascade) return;

      try {
        setLoading(true);
        // Exclui todos os motivos vinculados primeiro
        const deletePromises = linkedProbs.map((p) => 
          pb.collection("producao_problemas").delete(p.id)
        );
        await Promise.all(deletePromises);

        // Agora exclui a categoria
        await pb.collection("producao_problema_categorias").delete(id);
        await loadConfigData();
        alert("Categoria e motivos vinculados excluídos com sucesso!");
      } catch (err) {
        alert("Erro ao excluir categoria em cascata: " + err.message);
      } finally {
        setLoading(false);
      }
    } else {
      if (!window.confirm("Excluir esta categoria?")) return;
      try {
        await pb.collection("producao_problema_categorias").delete(id);
        await loadConfigData();
      } catch (err) {
        alert("Erro ao excluir categoria: " + err.message);
      }
    }
  };

  // --- CRUD MOTIVOS (PROBLEMAS) ---
  const handleAddProblem = async (e) => {
    e.preventDefault();
    if (!novoProbNome.trim() || !novoProbCatId) {
      alert("Preencha o nome e selecione uma categoria!");
      return;
    }

    const escopos = [];
    if (novoProbVerificar) escopos.push("VERIFICAR");
    if (novoProbFalhaBanca) escopos.push("FALHA_BANCA");

    if (escopos.length === 0) {
      alert("Selecione pelo menos um escopo (Verificar ou Falha Banca)!");
      return;
    }

    try {
      await pb.collection("producao_problemas").create({
        nome: novoProbNome.toUpperCase().trim(),
        categoria: novoProbCatId,
        escopos: escopos,
        obs_obrigatoria: novoProbObs,
        ativo: true,
      });
      setNovoProbNome("");
      setNovoProbCatId("");
      setNovoProbVerificar(false);
      setNovoProbFalhaBanca(false);
      setNovoProbObs(false);
      await loadConfigData();
    } catch (err) {
      const details = err.data ? "\nDetalhes: " + JSON.stringify(err.data) : "";
      alert("Erro ao criar motivo: " + err.message + details);
    }
  };

  const handleOpenEditProblem = (prob) => {
    setEditingProblem({
      id: prob.id,
      nome: prob.nome,
      categoria: prob.categoria,
      verificar: prob.escopos?.includes("VERIFICAR") || false,
      falhaBanca: prob.escopos?.includes("FALHA_BANCA") || false,
      obs_obrigatoria: prob.obs_obrigatoria || false,
      ativo: prob.ativo || false,
    });
  };

  const handleSaveEditProblem = async (e) => {
    e.preventDefault();
    if (!editingProblem.nome.trim() || !editingProblem.categoria) return;

    const escopos = [];
    if (editingProblem.verificar) escopos.push("VERIFICAR");
    if (editingProblem.falhaBanca) escopos.push("FALHA_BANCA");

    if (escopos.length === 0) {
      alert("Selecione pelo menos um escopo!");
      return;
    }

    try {
      await pb.collection("producao_problemas").update(editingProblem.id, {
        nome: editingProblem.nome.toUpperCase().trim(),
        categoria: editingProblem.categoria,
        escopos: escopos,
        obs_obrigatoria: editingProblem.obs_obrigatoria,
        ativo: editingProblem.ativo,
      });
      setEditingProblem(null);
      await loadConfigData();
    } catch (err) {
      const details = err.data ? "\nDetalhes: " + JSON.stringify(err.data) : "";
      alert("Erro ao atualizar motivo: " + err.message + details);
    }
  };

  const handleToggleProblemActive = async (prob) => {
    try {
      await pb.collection("producao_problemas").update(prob.id, {
        ativo: !prob.ativo,
      });
      await loadConfigData();
    } catch (err) {
      alert("Erro ao mudar status do motivo: " + err.message);
    }
  };

  const handleDeleteProblem = async (id) => {
    if (!window.confirm("Excluir este motivo definitivamente?")) return;
    try {
      await pb.collection("producao_problemas").delete(id);
      await loadConfigData();
    } catch (err) {
      alert("Erro ao excluir motivo: " + err.message);
    }
  };


  // --- FILTRAGEM LOCAL DASHBOARD ---
  const eventosFiltrados = useMemo(() => {
    return eventos.filter((ev) => {
      if (statusFiltro !== "all" && ev.status_destino !== statusFiltro) return false;
      if (dataInicio || dataFim) {
        const evDate = new Date(ev.created).toISOString().split("T")[0];
        if (dataInicio && evDate < dataInicio) return false;
        if (dataFim && evDate > dataFim) return false;
      }
      if (origemFiltro !== "all" && ev.status_anterior !== origemFiltro) return false;
      
      const cat = ev.expand?.problema?.expand?.categoria?.nome || "GERAL";
      if (categoriaFiltro !== "all" && cat !== categoriaFiltro) return false;

      return true;
    });
  }, [eventos, statusFiltro, dataInicio, dataFim, origemFiltro, categoriaFiltro]);

  const listaOrigens = useMemo(() => {
    const list = eventos.map((e) => e.status_anterior).filter(Boolean);
    return Array.from(new Set(list));
  }, [eventos]);

  const listaCategorias = useMemo(() => {
    const list = eventos.map((e) => e.expand?.problema?.expand?.categoria?.nome).filter(Boolean);
    return Array.from(new Set(list));
  }, [eventos]);

  // --- ANÁLISE POR MODELO (DASHBOARD HIERÁRQUICO) ---
  const modelStats = useMemo(() => {
    const stats = {};
    eventosFiltrados.forEach((ev) => {
      const modelName = getModelFromSku(ev.sku);
      const probName = ev.expand?.problema?.nome || "NÃO INFORMADO";
      const destStatus = ev.status_destino || "VERIFICAR";
      const origStatus = ev.status_anterior || "DESCONHECIDO";
      
      if (!stats[modelName]) {
        stats[modelName] = {
          model: modelName,
          count: 0,
          motivos: {},
          estagios: {},
          origens: {},
          eventos: [],
        };
      }
      
      stats[modelName].count++;
      stats[modelName].motivos[probName] = (stats[modelName].motivos[probName] || 0) + 1;
      stats[modelName].estagios[destStatus] = (stats[modelName].estagios[destStatus] || 0) + 1;
      stats[modelName].origens[origStatus] = (stats[modelName].origens[origStatus] || 0) + 1;
      
      stats[modelName].eventos.push({
        id: ev.id,
        created: ev.created,
        sku: ev.sku,
        order_number: ev.order_number,
        status_anterior: ev.status_anterior,
        status_destino: ev.status_destino,
        problema: probName,
        registrado_por: ev.registrado_por_operador
          ? `OFICINA · ${ev.registrado_por_operador}`
          : (ev.expand?.registrado_por?.name || "Sistema"),
      });
    });

    // Calcula os principais motivos e estágios para cada modelo
    const list = Object.values(stats).map((m) => {
      const topMotivo = Object.keys(m.motivos).sort((a, b) => m.motivos[b] - m.motivos[a])[0] || "N/I";
      const topEstagio = Object.keys(m.estagios).sort((a, b) => m.estagios[b] - m.estagios[a])[0] || "N/I";
      return {
        ...m,
        topMotivo,
        topEstagio,
      };
    });

    return list.sort((a, b) => b.count - a.count);
  }, [eventosFiltrados, getModelFromSku]);

  const filteredModelStats = useMemo(() => {
    return modelStats.filter((m) => 
      m.model.toLowerCase().includes(searchModelName.toLowerCase())
    );
  }, [modelStats, searchModelName]);

  const selectedModelData = useMemo(() => {
    if (!selectedModelReport) return null;
    return modelStats.find((m) => m.model === selectedModelReport) || null;
  }, [modelStats, selectedModelReport]);

  // --- FILTRAGEM MOTIVOS CONFIG ---
  const problemasConfigFiltrados = useMemo(() => {
    return problemasConfig.filter((p) => {
      const matchSearch = p.nome.toLowerCase().includes(searchMotive.toLowerCase());
      const matchCat = filterMotiveCat === "all" || p.categoria === filterMotiveCat;
      return matchSearch && matchCat;
    });
  }, [problemasConfig, searchMotive, filterMotiveCat]);


  // --- DADOS ANALÍTICOS DASHBOARD ---
  const paretoData = useMemo(() => {
    if (eventosFiltrados.length === 0) return [];
    if (drilldownCategoria) {
      const counts = {};
      eventosFiltrados
        .filter((ev) => {
          const cat = ev.expand?.problema?.expand?.categoria?.nome || "GERAL";
          return cat === drilldownCategoria;
        })
        .forEach((ev) => {
          const name = ev.expand?.problema?.nome || "NÃO INFORMADO";
          counts[name] = (counts[name] || 0) + 1;
        });

      const sorted = Object.keys(counts)
        .map((key) => ({ name: key, count: counts[key] }))
        .sort((a, b) => b.count - a.count);

      const total = sorted.reduce((sum, item) => sum + item.count, 0);
      let cumulativeSum = 0;
      return sorted.map((item) => {
        cumulativeSum += item.count;
        return {
          name: item.name,
          Quantidade: item.count,
          "Acumulado %": Math.round((cumulativeSum / total) * 100),
        };
      });
    } else {
      const counts = {};
      eventosFiltrados.forEach((ev) => {
        const cat = ev.expand?.problema?.expand?.categoria?.nome || "GERAL";
        counts[cat] = (counts[cat] || 0) + 1;
      });

      const sorted = Object.keys(counts)
        .map((key) => ({ name: key, count: counts[key] }))
        .sort((a, b) => b.count - a.count);

      const total = sorted.reduce((sum, item) => sum + item.count, 0);
      let cumulativeSum = 0;
      return sorted.map((item) => {
        cumulativeSum += item.count;
        return {
          name: item.name,
          Quantidade: item.count,
          "Acumulado %": Math.round((cumulativeSum / total) * 100),
        };
      });
    }
  }, [eventosFiltrados, drilldownCategoria]);

  const origemChartData = useMemo(() => {
    const counts = {};
    eventosFiltrados.forEach((ev) => {
      const orig = ev.status_anterior || "N/I";
      counts[orig] = (counts[orig] || 0) + 1;
    });

    return Object.keys(counts)
      .map((key) => {
        const config = PRODUCTION_STATUS_CONFIG[key] || {};
        return {
          status: config.label || key,
          Quantidade: counts[key],
        };
      })
      .sort((a, b) => b.Quantidade - a.Quantidade)
      .slice(0, 8);
  }, [eventosFiltrados]);

  const evolucaoData = useMemo(() => {
    const counts = {};
    eventosFiltrados.forEach((ev) => {
      const dateStr = new Date(ev.created).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
      });
      if (!counts[dateStr]) {
        counts[dateStr] = { data: dateStr, VERIFICAR: 0, FALHA_BANCA: 0 };
      }
      if (ev.status_destino in counts[dateStr]) {
        counts[dateStr][ev.status_destino]++;
      }
    });
    return Object.values(counts).reverse();
  }, [eventosFiltrados]);

  const reincidenciaTabela = useMemo(() => {
    const items = {};
    eventosFiltrados.forEach((ev) => {
      if (!items[ev.sku]) {
        items[ev.sku] = {
          sku: ev.sku,
          pedido: ev.order_number,
          passagens: ev.qtd_passagens || 1,
          ultimo_status: ev.status_destino,
          motivos: new Set(),
        };
      } else {
        if (ev.qtd_passagens > items[ev.sku].passagens) {
          items[ev.sku].passagens = ev.qtd_passagens;
          items[ev.sku].ultimo_status = ev.status_destino;
        }
      }
      if (ev.expand?.problema?.nome) {
        items[ev.sku].motivos.add(ev.expand.problema.nome);
      }
    });

    return Object.values(items)
      .filter((i) => i.passagens > 1)
      .sort((a, b) => b.passagens - a.passagens)
      .slice(0, 10);
  }, [eventosFiltrados]);

  const loteHistory = useMemo(() => {
    const lotes = {};
    eventosFiltrados.forEach((ev) => {
      if (!ev.lote_evento) return;
      if (!lotes[ev.lote_evento]) {
        lotes[ev.lote_evento] = {
          lote: ev.lote_evento,
          data: new Date(ev.created).toLocaleString("pt-BR"),
          total: 0,
          status: ev.status_destino,
          motivos: {},
        };
      }
      lotes[ev.lote_evento].total++;
      const mName = ev.expand?.problema?.nome || "NÃO INFORMADO";
      lotes[ev.lote_evento].motivos[mName] = (lotes[ev.lote_evento].motivos[mName] || 0) + 1;
    });

    return Object.values(lotes)
      .sort((a, b) => b.lote.localeCompare(a.lote))
      .slice(0, 10);
  }, [eventosFiltrados]);

  const matrizData = useMemo(() => {
    const categoriasMatriz = listaCategorias.length > 0 ? listaCategorias : ["GERAL"];
    const colunas = listaOrigens.length > 0 ? listaOrigens : ["REVISAO"];

    const matriz = {};
    categoriasMatriz.forEach((cat) => {
      matriz[cat] = {};
      colunas.forEach((col) => {
        matriz[cat][col] = 0;
      });
    });

    eventosFiltrados.forEach((ev) => {
      const cat = ev.expand?.problema?.expand?.categoria?.nome || "GERAL";
      const col = ev.status_anterior || "N/I";
      if (matriz[cat] && col in matriz[cat]) {
        matriz[cat][col]++;
      } else {
        if (!matriz[cat]) matriz[cat] = {};
        matriz[cat][col] = (matriz[cat][col] || 0) + 1;
      }
    });

    return {
      linhas: Object.keys(matriz),
      colunas: colunas.sort(),
      matriz,
    };
  }, [eventosFiltrados, listaCategorias, listaOrigens]);

  const fluxoTaxaData = useMemo(() => {
    const etapas = {};
    fluxoResumos.forEach((res) => {
      const orig = res.status_origem;
      if (!etapas[orig]) {
        etapas[orig] = { origem: orig, total: 0, verificar: 0, falhaBanca: 0 };
      }
      etapas[orig].total += res.total_saidas || 0;
      if (res.saidas_monitoradas) {
        etapas[orig].verificar += res.saidas_monitoradas.VERIFICAR || 0;
        etapas[orig].falhaBanca += res.saidas_monitoradas.FALHA_BANCA || 0;
      }
    });
    return etapas;
  }, [fluxoResumos]);

  const cardsMetrics = useMemo(() => {
    const total = eventosFiltrados.length;
    const preenchidos = eventosFiltrados.filter((e) => e.preenchido).length;
    const taxa = total > 0 ? Math.round((preenchidos / total) * 100) : 0;

    const motivosCounts = {};
    eventosFiltrados.forEach((ev) => {
      const mName = ev.expand?.problema?.nome || "NÃO INFORMADO";
      if (mName !== "NÃO INFORMADO") {
        motivosCounts[mName] = (motivosCounts[mName] || 0) + 1;
      }
    });
    const topMotivo = Object.keys(motivosCounts).sort((a, b) => motivosCounts[b] - motivosCounts[a])[0] || "Nenhum";

    const skusSet = new Set();
    const repitidos = new Set();
    eventosFiltrados.forEach((e) => {
      if (skusSet.has(e.sku)) repitidos.add(e.sku);
      else skusSet.add(e.sku);
    });

    return { total, taxa, topMotivo, reincidentes: repitidos.size };
  }, [eventosFiltrados]);

  return (
    <div className="flex-1 overflow-y-auto p-4 bg-slate-50 space-y-6 custom-scrollbar h-full">
      
      {/* EDIT MOTIVE OVERLAY MODAL */}
      {editingProblem && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[110] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md border border-slate-100 p-6 space-y-4">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-800 text-sm flex items-center gap-1.5">
                <Edit size={16} /> Editar Motivo
              </h3>
              <button 
                onClick={() => setEditingProblem(null)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSaveEditProblem} className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-slate-500 uppercase mb-1">Nome:</label>
                <input
                  type="text"
                  required
                  className="w-full px-3 py-2 border rounded-lg outline-none focus:border-slate-800 uppercase"
                  value={editingProblem.nome}
                  onChange={(e) => setEditingProblem({ ...editingProblem, nome: e.target.value })}
                />
              </div>

              <div>
                <label className="block font-bold text-slate-500 uppercase mb-1">Categoria:</label>
                <select
                  required
                  className="w-full px-3 py-2 border rounded-lg bg-white outline-none focus:border-slate-800"
                  value={editingProblem.categoria}
                  onChange={(e) => setEditingProblem({ ...editingProblem, categoria: e.target.value })}
                >
                  {categorias.map((c) => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2 bg-slate-50 p-3 rounded-lg border">
                <span className="block font-bold text-slate-500 uppercase mb-1">Configurações de Exibição:</span>
                
                <label className="flex items-center gap-2 cursor-pointer font-medium text-slate-700">
                  <input
                    type="checkbox"
                    checked={editingProblem.verificar}
                    onChange={(e) => setEditingProblem({ ...editingProblem, verificar: e.target.checked })}
                  />
                  Disponível em "VERIFICAR"
                </label>

                <label className="flex items-center gap-2 cursor-pointer font-medium text-slate-700">
                  <input
                    type="checkbox"
                    checked={editingProblem.falhaBanca}
                    onChange={(e) => setEditingProblem({ ...editingProblem, falhaBanca: e.target.checked })}
                  />
                  Disponível em "FALHA BANCA"
                </label>

                <div className="h-px bg-slate-200 my-1"></div>

                <label className="flex items-center gap-2 cursor-pointer font-medium text-slate-700">
                  <input
                    type="checkbox"
                    checked={editingProblem.obs_obrigatoria}
                    onChange={(e) => setEditingProblem({ ...editingProblem, obs_obrigatoria: e.target.checked })}
                  />
                  Exige observação obrigatória
                </label>

                <label className="flex items-center gap-2 cursor-pointer font-medium text-slate-700">
                  <input
                    type="checkbox"
                    checked={editingProblem.ativo}
                    onChange={(e) => setEditingProblem({ ...editingProblem, ativo: e.target.checked })}
                  />
                  Motivo ativo
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setEditingProblem(null)}
                  className="border px-4 py-2 rounded-lg font-bold hover:bg-slate-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-lg font-bold"
                >
                  Salvar Motivo
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CARD DE CABEÇALHO */}
      <div className="bg-slate-900 rounded-2xl p-6 text-white flex flex-col md:flex-row justify-between items-start md:items-center gap-4 shadow-lg">
        <div className="flex items-center gap-3">
          <div className="bg-red-500 text-white p-3 rounded-xl shadow-md">
            <ShieldAlert size={28} />
          </div>
          <div>
            <h2 className="font-bold text-xl leading-tight">Controle de Qualidade</h2>
            <p className="text-slate-400 text-xs mt-1">
              Desvios, Pareto, retrabalho e configurações do catálogo de problemas.
            </p>
          </div>
        </div>
        
        {/* TAB SWITCHER */}
        <div className="flex items-center bg-white/10 rounded-xl p-1 border border-white/10 shrink-0">
          <button
            onClick={() => setQcSubTab("dash")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
              qcSubTab === "dash"
                ? "bg-white text-slate-900 shadow"
                : "text-slate-300 hover:text-white"
            }`}
          >
            <BarChart2 size={14} /> Estatísticas
          </button>
          <button
            onClick={() => setQcSubTab("modelos")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
              qcSubTab === "modelos"
                ? "bg-white text-slate-900 shadow"
                : "text-slate-300 hover:text-white"
            }`}
          >
            <Package size={14} /> Análise por Modelo
          </button>
          <button
            onClick={() => setQcSubTab("config")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
              qcSubTab === "config"
                ? "bg-white text-slate-900 shadow"
                : "text-slate-300 hover:text-white"
            }`}
          >
            <Settings size={14} /> Configurar Motivos
          </button>
        </div>
      </div>

      {qcSubTab === "dash" && (
        // ======================= VIEW 1: DASHBOARD ESTATÍSTICO =======================
        <>
          {/* FILTROS */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-4">
            <div className="flex items-center gap-2 text-slate-700 font-bold text-xs uppercase border-b border-slate-100 pb-2">
              <Filter size={15} /> Filtros de Análise
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3.5">
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Status Destino:</label>
                <select
                  className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs bg-white outline-none focus:border-slate-800 font-bold text-slate-700"
                  value={statusFiltro}
                  onChange={(e) => setStatusFiltro(e.target.value)}
                >
                  <option value="all">Todos os Status</option>
                  <option value="VERIFICAR">VERIFICAR (Checagens)</option>
                  <option value="FALHA_BANCA">FALHA BANCA (Reprovações)</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Detectado Em (Origem):</label>
                <select
                  className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs bg-white outline-none focus:border-slate-800 text-slate-700 font-medium"
                  value={origemFiltro}
                  onChange={(e) => setOrigemFiltro(e.target.value)}
                >
                  <option value="all">Qualquer Etapa</option>
                  {listaOrigens.map((o) => (
                    <option key={o} value={o}>
                      {PRODUCTION_STATUS_CONFIG[o]?.label || o}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Nascido Em (Estágio):</label>
                <select
                  className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs bg-white outline-none focus:border-slate-800 text-slate-700 font-medium"
                  value={categoriaFiltro}
                  onChange={(e) => setCategoriaFiltro(e.target.value)}
                >
                  <option value="all">Qualquer Estágio</option>
                  {listaCategorias.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Período:</label>
                <select
                  className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs bg-white outline-none focus:border-slate-800 text-slate-700 font-medium"
                  value={presetPeriodo}
                  onChange={(e) => setPresetPeriodo(e.target.value)}
                >
                  <option value="7">Últimos 7 dias</option>
                  <option value="30">Últimos 30 dias</option>
                  <option value="90">Últimos 90 dias</option>
                  <option value="custom">Período Customizado</option>
                </select>
              </div>

              {presetPeriodo === "custom" && (
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">De:</label>
                    <input
                      type="date"
                      className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-xs outline-none focus:border-slate-800 text-slate-700"
                      value={dataInicio}
                      onChange={(e) => setDataInicio(e.target.value)}
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Até:</label>
                    <input
                      type="date"
                      className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-xs outline-none focus:border-slate-800 text-slate-700"
                      value={dataFim}
                      onChange={(e) => setDataFim(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* CARDS MÉTRICAS */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex items-center justify-between">
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Total Desvios</span>
                <span className="text-2xl font-bold text-slate-800 mt-1 block">{cardsMetrics.total}</span>
                <span className="text-[10px] text-slate-400 font-medium">no período selecionado</span>
              </div>
              <div className="bg-red-50 text-red-500 p-3 rounded-lg">
                <Activity size={22} />
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex items-center justify-between">
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Taxa Adesão Form</span>
                <span className="text-2xl font-bold text-slate-800 mt-1 block">{cardsMetrics.taxa}%</span>
                <span className="text-[10px] text-slate-400 font-medium">de preenchimento de motivos</span>
              </div>
              <div className="bg-emerald-50 text-emerald-600 p-3 rounded-lg">
                <Clock size={22} />
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex items-center justify-between">
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Maior Reclamado</span>
                <span className="text-sm font-bold text-slate-800 mt-2 block truncate max-w-[160px]" title={cardsMetrics.topMotivo}>
                  {cardsMetrics.topMotivo}
                </span>
                <span className="text-[10px] text-slate-400 font-medium">excluindo "Não Informado"</span>
              </div>
              <div className="bg-amber-50 text-amber-600 p-3 rounded-lg">
                <AlertTriangle size={22} />
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex items-center justify-between">
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Itens Reincidentes</span>
                <span className="text-2xl font-bold text-slate-800 mt-1 block">{cardsMetrics.reincidentes}</span>
                <span className="text-[10px] text-slate-400 font-medium">passaram + de uma vez no status</span>
              </div>
              <div className="bg-indigo-50 text-indigo-600 p-3 rounded-lg">
                <Layers size={22} />
              </div>
            </div>
          </div>

          {/* GRÁFICOS */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* PARETO */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex flex-col h-[400px]">
              <div className="flex justify-between items-center mb-4 shrink-0">
                <div>
                  <h4 className="text-xs font-bold text-slate-700 uppercase flex items-center gap-1.5">
                    <BarChart2 size={16} /> Pareto de Causas e Desvios
                  </h4>
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    {drilldownCategoria 
                      ? `Exibindo motivos para: ${drilldownCategoria}`
                      : "Por Estágio Originador (Clique na barra para detalhar)"
                    }
                  </p>
                </div>
                {drilldownCategoria && (
                  <button
                    onClick={() => setDrilldownCategoria(null)}
                    className="text-[10px] font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1 bg-blue-50 px-2 py-1 rounded-lg border border-blue-200"
                  >
                    <ArrowLeft size={12} /> Voltar Geral
                  </button>
                )}
              </div>

              <div className="flex-1 min-h-0">
                {paretoData.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-xs text-slate-400">
                    Sem desvios cadastrados no período.
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={paretoData}
                      margin={{ top: 10, right: 10, left: -10, bottom: 5 }}
                      onClick={(state) => {
                        if (!drilldownCategoria && state && state.activePayload) {
                          const clickedName = state.activePayload[0].payload.name;
                          setDrilldownCategoria(clickedName);
                        }
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis 
                        dataKey="name" 
                        tick={{ fontSize: 9, fill: "#64748b", fontWeight: "bold" }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis yAxisId="left" tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} />
                      <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                      <Bar 
                        yAxisId="left" 
                        dataKey="Quantidade" 
                        fill="#3b82f6" 
                        radius={[4, 4, 0, 0]} 
                        barSize={32}
                        cursor={!drilldownCategoria ? "pointer" : "default"}
                      />
                      <Line 
                        yAxisId="right" 
                        type="monotone" 
                        dataKey="Acumulado %" 
                        stroke="#ef4444" 
                        strokeWidth={2.5}
                        dot={{ stroke: "#ef4444", strokeWidth: 2, r: 3 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* EVOLUÇÃO HISTÓRICA */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex flex-col h-[400px]">
              <div className="mb-4 shrink-0">
                <h4 className="text-xs font-bold text-slate-700 uppercase flex items-center gap-1.5">
                  <TrendingUp size={16} /> Evolução Histórica
                </h4>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  Ocorrência de desvios e reprovações agrupados por dia.
                </p>
              </div>

              <div className="flex-1 min-h-0">
                {evolucaoData.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-xs text-slate-400">
                    Sem histórico disponível para os filtros atuais.
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={evolucaoData} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="data" tick={{ fontSize: 9, fill: "#64748b" }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                      <Bar dataKey="VERIFICAR" stackId="a" fill="#ec4899" name="Verificar" />
                      <Bar dataKey="FALHA_BANCA" stackId="a" fill="#ef4444" radius={[4, 4, 0, 0]} name="Falha Banca" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>

          {/* MATRIZ */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-4">
            <div>
              <h4 className="text-xs font-bold text-slate-700 uppercase flex items-center gap-1.5">
                <AlertCircle size={16} /> Matriz de Retrabalho (Onde Nascido vs Onde Detectado)
              </h4>
              <p className="text-[10px] text-slate-400 mt-0.5">
                Células fora da diagonal representam etapas produtivas trabalhando em peças já defeituosas anteriormente.
              </p>
            </div>

            {matrizData.linhas.length === 0 ? (
              <div className="py-6 text-center text-xs text-slate-400">Nenhum cruzamento encontrado.</div>
            ) : (
              <div className="overflow-x-auto border border-slate-100 rounded-lg">
                <table className="w-full text-left border-collapse text-xs">
                  <thead className="bg-slate-50 text-slate-600 font-bold uppercase">
                    <tr>
                      <th className="p-3 border-b">Estágio Originador ↓</th>
                      {matrizData.colunas.map((col) => (
                        <th key={col} className="p-3 border-b text-center min-w-[100px]">
                          {PRODUCTION_STATUS_CONFIG[col]?.label || col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {matrizData.linhas.map((linha) => (
                      <tr key={linha} className="hover:bg-slate-50">
                        <td className="p-3 font-bold text-slate-800 bg-slate-50/50">{linha}</td>
                        {matrizData.colunas.map((col) => {
                          const valor = matrizData.matriz[linha][col] || 0;
                          let bgClass = "bg-white text-slate-400";
                          if (valor > 0) {
                            if (valor < 3) bgClass = "bg-amber-100/60 text-amber-800 font-semibold";
                            else if (valor < 8) bgClass = "bg-orange-100 text-orange-950 font-bold";
                            else bgClass = "bg-red-100 text-red-950 font-bold ring-1 ring-inset ring-red-200";
                          }
                          return (
                            <td key={col} className={`p-3 text-center transition-colors ${bgClass}`}>{valor}</td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* TABELAS COMPLEMENTARES */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* ETAPAS DE ORIGEM */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex flex-col h-[340px]">
              <h4 className="text-xs font-bold text-slate-700 uppercase mb-4 flex items-center gap-1.5 shrink-0">
                <Activity size={16} /> Etapas de Origem (Principais)
              </h4>
              <div className="flex-1 min-h-0">
                {origemChartData.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-xs text-slate-400">Sem dados</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart layout="vertical" data={origemChartData}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                      <XAxis type="number" tick={{ fontSize: 9 }} axisLine={false} />
                      <YAxis dataKey="status" type="category" tick={{ fontSize: 9 }} width={90} axisLine={false} />
                      <Tooltip contentStyle={{ fontSize: 11 }} />
                      <Bar dataKey="Quantidade" fill="#a855f7" radius={[0, 4, 4, 0]} barSize={16} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* REPROVAÇÕES LOTE */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex flex-col h-[340px] overflow-hidden">
              <h4 className="text-xs font-bold text-slate-700 uppercase mb-3 shrink-0">Últimas em Lote</h4>
              <div className="flex-1 overflow-y-auto divide-y divide-slate-100 custom-scrollbar text-xs">
                {loteHistory.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-slate-400">Nenhum lote registrado.</div>
                ) : (
                  loteHistory.map((l) => {
                    const motivosStr = Object.keys(l.motivos).map((m) => `${m} (${l.motivos[m]})`).join(", ");
                    return (
                      <div key={l.lote} className="py-2.5 hover:bg-slate-50 rounded-lg px-1">
                        <div className="flex justify-between items-center font-bold">
                          <span className="text-slate-800">Lote: {l.lote.substring(0, 15)}...</span>
                          <span className={`px-2 py-0.5 rounded text-[10px] ${l.status === "FALHA_BANCA" ? "bg-red-500 text-white" : "bg-pink-400 text-white"}`}>
                            {l.total} itens
                          </span>
                        </div>
                        <div className="text-[10px] text-slate-400 mt-0.5">{l.data}</div>
                        <div className="text-[11px] text-slate-500 mt-1 truncate" title={motivosStr}>
                          <strong>Motivos:</strong> {motivosStr}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* REINCIDENTES */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex flex-col h-[340px] overflow-hidden">
              <h4 className="text-xs font-bold text-slate-700 uppercase mb-3 shrink-0">Pedidos Mais Reincidentes</h4>
              <div className="flex-1 overflow-y-auto custom-scrollbar text-xs">
                {reincidenciaTabela.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-slate-400">Sem reincidências no período.</div>
                ) : (
                  <table className="w-full text-left">
                    <thead>
                      <tr className="text-slate-400 border-b">
                        <th className="pb-2">SKU / Pedido</th>
                        <th className="pb-2 text-center">Entradas</th>
                        <th className="pb-2 text-right">Escopo</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {reincidenciaTabela.map((r) => (
                        <tr key={r.sku} className="hover:bg-slate-50">
                          <td className="py-2 font-bold text-slate-800">
                            <div>{r.sku}</div>
                            <div className="text-[9px] text-slate-400 font-normal">Nº {r.pedido}</div>
                          </td>
                          <td className="py-2 text-center font-bold text-red-500 bg-red-50/20">{r.passagens}x</td>
                          <td className="py-2 text-right">
                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${r.ultimo_status === "FALHA_BANCA" ? "bg-red-100 text-red-700" : "bg-pink-100 text-pink-700"}`}>
                              {r.ultimo_status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          {/* MAPA DE LEAKING */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-5">
            <div>
              <h4 className="text-xs font-bold text-slate-700 uppercase flex items-center gap-1.5">
                <Layers size={16} /> Mapa do Fluxo e Taxa de Desvios
              </h4>
              <p className="text-[10px] text-slate-400 mt-0.5">
                Porcentagem das saídas de cada etapa produtiva que vazam para VERIFICAR ou FALHA BANCA no mês corrente.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 xl:grid-cols-7 gap-3">
              {KANBAN_ORDER.map((statusId) => {
                const config = PRODUCTION_STATUS_CONFIG[statusId] || {};
                const stats = fluxoTaxaData[statusId] || { total: 0, verificar: 0, falhaBanca: 0 };
                
                const totalSaidas = stats.total;
                const totalDesvios = stats.verificar + stats.falhaBanca;
                const taxaVazamento = totalSaidas > 0 ? ((totalDesvios / totalSaidas) * 100).toFixed(1) : "0.0";
                
                let badgeColor = "bg-slate-100 text-slate-500 border-slate-200";
                if (totalSaidas > 0 && totalDesvios > 0) {
                  const numTaxa = parseFloat(taxaVazamento);
                  if (numTaxa < 2.0) badgeColor = "bg-emerald-50 text-emerald-700 border-emerald-200";
                  else if (numTaxa < 5.0) badgeColor = "bg-amber-50 text-amber-700 border-amber-250";
                  else badgeColor = "bg-red-50 text-red-700 border-red-200 animate-pulse";
                }

                return (
                  <div key={statusId} className="border border-slate-200 rounded-xl p-3 bg-slate-50/50 flex flex-col justify-between hover:shadow-md transition-shadow">
                    <div>
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full inline-block truncate max-w-full ${config.color || "bg-slate-400 text-white"}`}>
                        {config.label || statusId}
                      </span>
                    </div>

                    <div className="mt-4">
                      <div className="text-[10px] text-slate-400 font-medium">Saídas: <strong className="text-slate-700">{totalSaidas}</strong></div>
                      <div className="text-[10px] text-slate-400 font-medium">Desvios: <strong className="text-slate-700">{totalDesvios}</strong></div>
                    </div>

                    <div className={`mt-3 border text-center rounded-lg py-1.5 text-xs font-bold ${badgeColor}`}>
                      {taxaVazamento}% Leaking
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* ======================= VIEW 2: ANÁLISE POR MODELO (DASHBOARD HIERÁRQUICO) ======================= */}
      {qcSubTab === "modelos" && (
        <>
          {selectedModelReport ? (
            /* ======================= SUB-VIEW 2A: RELATÓRIO DO MODELO SELECIONADO ======================= */
            <div className="space-y-6">
              {/* HEADER E VOLTAR */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <button
                    onClick={() => setSelectedModelReport(null)}
                    className="flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:text-blue-800 bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-200"
                  >
                    <ArrowLeft size={14} /> Voltar para Lista de Modelos
                  </button>
                  <h3 className="font-bold text-slate-800 text-sm mt-3 flex items-center gap-2">
                    <Package size={22} className="text-slate-500" /> Relatório de Qualidade: Modelo <strong className="text-slate-900 underline">{selectedModelReport}</strong>
                  </h3>
                  <p className="text-[11px] text-slate-400 mt-1">
                    Detalhamento dos motivos e etapas onde este modelo falhou no período filtrado.
                  </p>
                </div>
              </div>

              {/* CARD RESUMOS RAPIDOS */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Falhas Registradas</span>
                  <span className="text-2xl font-bold text-slate-850 mt-1 block">{selectedModelData?.count || 0}x</span>
                  <span className="text-[10px] text-slate-400 font-medium">ocorrências no período</span>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Principal Tipo de Erro</span>
                  <span className="text-sm font-bold text-red-650 mt-2 block truncate max-w-full" title={selectedModelData?.topMotivo}>
                    {selectedModelData?.topMotivo || "N/A"}
                  </span>
                  <span className="text-[10px] text-slate-400 font-medium">maior causa de refugo</span>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Principal Detecção</span>
                  <span className="text-sm font-bold text-slate-850 mt-2 block truncate max-w-full" title={selectedModelData?.topEstagio}>
                    {PRODUCTION_STATUS_CONFIG[selectedModelData?.topEstagio]?.label || selectedModelData?.topEstagio || "N/A"}
                  </span>
                  <span className="text-[10px] text-slate-400 font-medium">etapa onde mais se detecta defeito</span>
                </div>
              </div>

              {/* GRAFICOS DO MODELO */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Pareto de Motivos */}
                <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex flex-col h-[350px]">
                  <h4 className="text-xs font-bold text-slate-700 uppercase mb-4 flex items-center gap-1.5 shrink-0">
                    <AlertTriangle size={16} /> Tipos de Erro (Porquê falhou)
                  </h4>
                  <div className="flex-1 min-h-0">
                    {selectedModelData && Object.keys(selectedModelData.motivos).length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={Object.keys(selectedModelData.motivos).map(key => ({
                            name: key,
                            Quantidade: selectedModelData.motivos[key]
                          })).sort((a,b) => b.Quantidade - a.Quantidade)}
                          margin={{ top: 10, right: 10, left: -15, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                          <XAxis dataKey="name" tick={{ fontSize: 9 }} axisLine={false} />
                          <YAxis tick={{ fontSize: 10 }} axisLine={false} />
                          <Tooltip contentStyle={{ fontSize: 11 }} />
                          <Bar dataKey="Quantidade" fill="#ef4444" radius={[4, 4, 0, 0]} barSize={24} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-xs text-slate-400">Sem motivos salvos</div>
                    )}
                  </div>
                </div>

                {/* Pareto de Estágios de Detecção */}
                <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex flex-col h-[350px]">
                  <h4 className="text-xs font-bold text-slate-700 uppercase mb-4 flex items-center gap-1.5 shrink-0">
                    <ShieldAlert size={16} /> Etapas de Detecção (Onde foi pego)
                  </h4>
                  <div className="flex-1 min-h-0">
                    {selectedModelData && Object.keys(selectedModelData.estagios).length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={Object.keys(selectedModelData.estagios).map(key => ({
                            name: PRODUCTION_STATUS_CONFIG[key]?.label || key,
                            Quantidade: selectedModelData.estagios[key]
                          })).sort((a,b) => b.Quantidade - a.Quantidade)}
                          margin={{ top: 10, right: 10, left: -15, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                          <XAxis dataKey="name" tick={{ fontSize: 9 }} axisLine={false} />
                          <YAxis tick={{ fontSize: 10 }} axisLine={false} />
                          <Tooltip contentStyle={{ fontSize: 11 }} />
                          <Bar dataKey="Quantidade" fill="#a855f7" radius={[4, 4, 0, 0]} barSize={24} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-xs text-slate-400">Sem estágios salvos</div>
                    )}
                  </div>
                </div>
              </div>

              {/* HISTORICO DO MODELO */}
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
                <h4 className="font-bold text-slate-700 text-xs uppercase flex items-center gap-1.5 border-b pb-2">
                  <Clock size={16} /> Histórico de Desvios deste Modelo
                </h4>
                <div className="overflow-x-auto rounded-lg border border-slate-100">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead className="bg-slate-50 text-slate-600 font-bold uppercase border-b">
                      <tr>
                        <th className="p-3">Data/Hora</th>
                        <th className="p-3">SKU</th>
                        <th className="p-3">Pedido</th>
                        <th className="p-3">Estágio Origem</th>
                        <th className="p-3">Estágio Detecção</th>
                        <th className="p-3">Motivo do Desvio</th>
                        <th className="p-3">Observações</th>
                        <th className="p-3">Registrado Por</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {selectedModelData?.eventos.map((ev) => (
                        <tr key={ev.id} className="hover:bg-slate-50 text-slate-700">
                          <td className="p-3 whitespace-nowrap text-slate-400">
                            {new Date(ev.created).toLocaleString("pt-BR")}
                          </td>
                          <td className="p-3 font-semibold text-slate-900">{ev.sku}</td>
                          <td className="p-3 font-medium text-slate-500">Nº {ev.order_number}</td>
                          <td className="p-3">
                            {PRODUCTION_STATUS_CONFIG[ev.status_anterior]?.label || ev.status_anterior || "N/I"}
                          </td>
                          <td className="p-3 font-bold text-slate-800">
                            {PRODUCTION_STATUS_CONFIG[ev.status_destino]?.label || ev.status_destino}
                          </td>
                          <td className="p-3 font-bold text-red-600">{ev.problema}</td>
                          <td className="p-3 max-w-[200px] truncate" title={ev.observacoes}>
                            {ev.observacoes || <em className="text-slate-400 font-normal">Nenhuma</em>}
                          </td>
                          <td className="p-3 text-slate-400">{ev.registrado_por}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            /* ======================= SUB-VIEW 2B: LISTA GERAL DE MODELOS COM DESVIOS ======================= */
            <div className="space-y-6">
              {/* FILTROS E MÉTRIQUE DE MODELOS */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm flex items-center justify-between">
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Modelos com Falhas</span>
                    <span className="text-2xl font-bold text-slate-800 mt-1 block">{modelStats.length}</span>
                    <span className="text-[10px] text-slate-400 font-medium">modelos distintos afetados</span>
                  </div>
                  <div className="bg-blue-50 text-blue-500 p-3 rounded-lg">
                    <Package size={22} />
                  </div>
                </div>

                <div className="bg-white border border-slate-200 p-5 shadow-sm rounded-xl flex items-center justify-between">
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Pior Modelo</span>
                    <span className="text-sm font-bold text-slate-800 mt-2 block truncate max-w-[150px]" title={modelStats[0]?.model}>
                      {modelStats[0]?.model || "Nenhum"}
                    </span>
                    <span className="text-[10px] text-red-500 font-medium font-bold">
                      {modelStats[0] ? `${modelStats[0].count} desvios` : "0 desvios"}
                    </span>
                  </div>
                  <div className="bg-red-50 text-red-500 p-3 rounded-lg">
                    <AlertTriangle size={22} />
                  </div>
                </div>

                {/* INPUT DE BUSCA */}
                <div className="bg-white border border-slate-200 p-5 shadow-sm rounded-xl flex flex-col justify-center">
                  <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Buscar Modelo/SKU:</label>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                    <input
                      type="text"
                      className="w-full pl-8 pr-3 py-1.5 border border-slate-300 rounded-lg text-xs outline-none focus:border-slate-800 font-medium text-slate-700"
                      placeholder="Filtrar por nome do modelo..."
                      value={searchModelName}
                      onChange={(e) => setSearchModelName(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* LISTA GERAL DE MODELOS */}
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="text-xs font-bold text-slate-700 uppercase flex items-center gap-1.5">
                    <BarChart2 size={16} /> Histórico de Perdas por Modelo
                  </h4>
                </div>

                <div className="overflow-x-auto rounded-xl border border-slate-100">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead className="bg-slate-50 text-slate-600 font-bold uppercase sticky top-0">
                      <tr className="border-b">
                        <th className="p-3">Modelo (SKU Pai)</th>
                        <th className="p-3 text-center">Frequência de Falhas</th>
                        <th className="p-3">Causa Principal (Motivo)</th>
                        <th className="p-3">Estágio onde Mais Falha</th>
                        <th className="p-3 text-center">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredModelStats.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="p-8 text-center text-slate-400 text-xs">
                            Nenhum modelo com falhas encontrado nos filtros aplicados.
                          </td>
                        </tr>
                      ) : (
                        filteredModelStats.map((item) => (
                          <tr key={item.model} className="hover:bg-slate-50 text-slate-700 transition-colors">
                            <td className="p-3 font-bold text-slate-900 flex items-center gap-2">
                              <span className="bg-slate-100 p-1.5 rounded-lg text-slate-500">
                                <Package size={14} />
                              </span>
                              <span>{item.model}</span>
                            </td>
                            <td className="p-3 text-center font-bold text-slate-800 bg-slate-50/30">
                              <span className="bg-red-50 text-red-700 px-2.5 py-1 rounded-full font-bold">
                                {item.count} falhas
                              </span>
                            </td>
                            <td className="p-3 font-semibold text-slate-600">
                              {item.topMotivo}
                            </td>
                            <td className="p-3">
                              <span className="bg-purple-50 text-purple-700 font-bold px-2 py-0.5 rounded text-[10px]">
                                {PRODUCTION_STATUS_CONFIG[item.topEstagio]?.label || item.topEstagio}
                              </span>
                            </td>
                            <td className="p-3 text-center">
                              <button
                                onClick={() => setSelectedModelReport(item.model)}
                                className="bg-slate-900 hover:bg-slate-850 text-white font-bold px-3 py-1.5 rounded-lg text-[10px] transition-colors"
                              >
                                Ver Detalhes
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {qcSubTab === "config" && (
        // ======================= VIEW 3: GERENCIADOR DE MOTIVOS (CRUD CONFIG) =======================
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          
          {/* COLUNA ESQUERDA: GERENCIAR CATEGORIAS */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
            <h3 className="font-bold text-slate-700 text-sm flex items-center gap-1.5 border-b pb-2">
              <Layers size={16} /> Estágios (Categorias)
            </h3>

            {/* Criar Categoria */}
            <form onSubmit={handleAddCategory} className="flex gap-2">
              <input
                type="text"
                required
                className="flex-1 px-3 py-1.5 border rounded-lg text-xs outline-none focus:border-slate-800 uppercase"
                placeholder="NOVA CATEGORIA..."
                value={novaCatNome}
                onChange={(e) => setNovaCatNome(e.target.value)}
              />
              <button
                type="submit"
                className="bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors"
              >
                <Plus size={14} /> Add
              </button>
            </form>

            {/* Lista de Categorias */}
            <div className="space-y-2 max-h-[450px] overflow-y-auto custom-scrollbar pr-1">
              {categorias.length === 0 ? (
                <div className="text-center py-6 text-xs text-slate-400">Nenhuma categoria cadastrada.</div>
              ) : (
                categorias.map((cat) => (
                  <div 
                    key={cat.id} 
                    className={`flex items-center justify-between p-2.5 rounded-lg border text-xs transition-all ${
                      cat.ativo ? "bg-slate-50 border-slate-100" : "bg-slate-100/50 border-slate-200 text-slate-400"
                    }`}
                  >
                    {editingCatId === cat.id ? (
                      <div className="flex items-center gap-1.5 w-full">
                        <input
                          type="text"
                          className="flex-1 px-2 py-1 border rounded text-xs outline-none focus:border-slate-800 uppercase"
                          value={editingCatNome}
                          onChange={(e) => setEditingCatNome(e.target.value)}
                        />
                        <button
                          onClick={() => handleUpdateCategory(cat.id)}
                          className="text-emerald-600 hover:text-emerald-800 p-1"
                        >
                          <Check size={16} />
                        </button>
                        <button
                          onClick={() => {
                            setEditingCatId(null);
                            setEditingCatNome("");
                          }}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <span className="font-bold uppercase truncate max-w-[130px]" title={cat.nome}>
                          {cat.nome}
                        </span>
                        
                        <div className="flex items-center gap-1 shrink-0">
                          {/* Toggle Active status */}
                          <button
                            onClick={() => handleToggleCategoryActive(cat)}
                            className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${
                              cat.ativo 
                                ? "bg-emerald-100 text-emerald-700" 
                                : "bg-slate-200 text-slate-600"
                            }`}
                          >
                            {cat.ativo ? "Ativo" : "Inativo"}
                          </button>
                          
                          {/* Edit button */}
                          <button
                            onClick={() => {
                              setEditingCatId(cat.id);
                              setEditingCatNome(cat.nome);
                            }}
                            className="text-slate-500 hover:text-slate-850 p-1"
                            title="Editar nome"
                          >
                            <Edit size={12} />
                          </button>

                          {/* Delete button */}
                          <button
                            onClick={() => handleDeleteCategory(cat.id)}
                            className="text-red-500 hover:text-red-700 p-1"
                            title="Excluir categoria"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* COLUNA DIREITA: GERENCIAR MOTIVOS */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4 lg:col-span-2">
            <h3 className="font-bold text-slate-700 text-sm flex items-center gap-1.5 border-b pb-2">
              <ShieldAlert size={16} /> Cadastro de Motivos (Problemas)
            </h3>

            {/* Criar Motivo Form */}
            <form onSubmit={handleAddProblem} className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-4 text-xs">
              <span className="font-bold text-slate-700 block">Novo Motivo de Desvio</span>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-500 font-bold uppercase mb-1">Nome:</label>
                  <input
                    type="text"
                    required
                    placeholder="Ex: PORO EVIDENTE, PEÇA INCOMPLETA"
                    className="w-full px-3 py-2 border rounded-lg outline-none focus:border-slate-800 uppercase"
                    value={novoProbNome}
                    onChange={(e) => setNovoProbNome(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-slate-500 font-bold uppercase mb-1">Categoria Vinculada:</label>
                  <select
                    required
                    className="w-full px-3 py-2 border rounded-lg bg-white outline-none focus:border-slate-800"
                    value={novoProbCatId}
                    onChange={(e) => setNovoProbCatId(e.target.value)}
                  >
                    <option value="" disabled>Selecione...</option>
                    {categorias.filter(c => c.ativo).map((c) => (
                      <option key={c.id} value={c.id}>{c.nome}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Checkboxes Config */}
              <div className="flex flex-wrap gap-x-6 gap-y-2 bg-white p-3 rounded-lg border border-slate-200">
                <label className="flex items-center gap-1.5 font-semibold text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={novoProbVerificar}
                    onChange={(e) => setNovoProbVerificar(e.target.checked)}
                  />
                  Aparece em VERIFICAR
                </label>

                <label className="flex items-center gap-1.5 font-semibold text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={novoProbFalhaBanca}
                    onChange={(e) => setNovoProbFalhaBanca(e.target.checked)}
                  />
                  Aparece em FALHA BANCA
                </label>

                <div className="w-px bg-slate-300 self-stretch hidden md:block"></div>

                <label className="flex items-center gap-1.5 font-semibold text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={novoProbObs}
                    onChange={(e) => setNovoProbObs(e.target.checked)}
                  />
                  Exige Observação Obrigatória
                </label>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  className="bg-slate-900 hover:bg-slate-850 text-white font-bold px-5 py-2.5 rounded-lg flex items-center gap-1.5 transition-colors shadow-sm"
                >
                  <Plus size={16} /> Criar Motivo
                </button>
              </div>
            </form>

            {/* Filtros e Lista de Motivos */}
            <div className="space-y-3 pt-2">
              <div className="flex flex-col sm:flex-row gap-3 justify-between items-center text-xs">
                {/* Search */}
                <input
                  type="text"
                  placeholder="Pesquisar motivo pelo nome..."
                  className="w-full sm:max-w-xs px-3 py-1.5 border rounded-lg outline-none focus:border-slate-850"
                  value={searchMotive}
                  onChange={(e) => setSearchMotive(e.target.value)}
                />

                {/* Filter by category */}
                <div className="flex items-center gap-1.5 w-full sm:w-auto shrink-0">
                  <span className="text-slate-400 font-bold uppercase text-[10px]">Categoria:</span>
                  <select
                    className="flex-1 sm:w-[150px] px-2 py-1.5 border rounded-lg bg-white outline-none focus:border-slate-850"
                    value={filterMotiveCat}
                    onChange={(e) => setFilterMotiveCat(e.target.value)}
                  >
                    <option value="all">Todas</option>
                    {categorias.map((c) => (
                      <option key={c.id} value={c.id}>{c.nome}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Tabela de Motivos */}
              <div className="border rounded-xl overflow-hidden shadow-sm max-h-[350px] overflow-y-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead className="bg-slate-100 text-slate-600 font-bold uppercase sticky top-0">
                    <tr>
                      <th className="p-3 border-b">Motivo</th>
                      <th className="p-3 border-b">Categoria (Estágio)</th>
                      <th className="p-3 border-b">Escopos</th>
                      <th className="p-3 border-b text-center">Obs Obrigatória?</th>
                      <th className="p-3 border-b text-center">Status</th>
                      <th className="p-3 border-b text-center">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {problemasConfigFiltrados.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="p-8 text-center text-slate-400 text-xs">
                          Nenhum motivo cadastrado ou correspondente aos filtros.
                        </td>
                      </tr>
                    ) : (
                      problemasConfigFiltrados.map((prob) => {
                        const isNaoInformado = prob.nome === "NÃO INFORMADO";
                        return (
                          <tr key={prob.id} className={`hover:bg-slate-50 ${prob.ativo ? "" : "opacity-60 text-slate-400"}`}>
                            <td className="p-3 font-bold text-slate-800">
                              {prob.nome}
                            </td>
                            <td className="p-3 text-slate-500 font-medium">
                              {prob.expand?.categoria?.nome || "SEM CATEGORIA"}
                            </td>
                            <td className="p-3 space-x-1 shrink-0">
                              {(Array.isArray(prob.escopos) ? prob.escopos : (typeof prob.escopos === "string" ? [prob.escopos] : [])).map((e) => (
                                <span 
                                  key={e} 
                                  className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                                    e === "VERIFICAR" ? "bg-pink-100 text-pink-700" : "bg-red-100 text-red-700"
                                  }`}
                                >
                                  {e}
                                </span>
                              ))}
                            </td>
                            <td className="p-3 text-center">
                              <span className={`px-2 py-0.5 rounded text-[9px] font-bold ${
                                prob.obs_obrigatoria ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"
                              }`}>
                                {prob.obs_obrigatoria ? "Sim" : "Não"}
                              </span>
                            </td>
                            <td className="p-3 text-center">
                              {isNaoInformado ? (
                                <span className="bg-slate-200 text-slate-600 px-2 py-0.5 rounded text-[9px] font-bold">Fixo</span>
                              ) : (
                                <button
                                  onClick={() => handleToggleProblemActive(prob)}
                                  className={`px-2 py-0.5 rounded text-[9px] font-bold ${
                                    prob.ativo ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"
                                  }`}
                                >
                                  {prob.ativo ? "Ativo" : "Inativo"}
                                </button>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              {isNaoInformado ? (
                                <span className="text-[10px] text-slate-400 font-medium">Sem ações</span>
                              ) : (
                                <div className="flex justify-center gap-1.5">
                                  <button
                                    onClick={() => handleOpenEditProblem(prob)}
                                    className="text-slate-500 hover:text-slate-800 p-1"
                                    title="Editar motivo"
                                  >
                                    <Edit size={13} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteProblem(prob.id)}
                                    className="text-red-500 hover:text-red-700 p-1"
                                    title="Excluir motivo"
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

        </div>
      )}

    </div>
  );
}
